import { Injectable, computed, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import {
  ReductionActionCode,
  ReductionActionDef,
  TransportAlternativeOptions,
} from '../model/reduction-action';
import { CurrentUserService } from './current-user.service';

/**
 * Reduction actions the company commits to — the reduction tips highlighted on
 * every trip report. Unrelated to custom actions and SDGs, which are purely
 * declarative and have their own services.
 */
@Injectable({ providedIn: 'root' })
export class ReductionActionsService {
  private http = inject(HttpClient);
  private currentUserService = inject(CurrentUserService);

  reductionActions = computed<ReductionActionCode[]>(
    () =>
      (this.currentUserService.currentUser()?.company?.reductionActions ??
        []) as ReductionActionCode[],
  );

  getCatalogue(): Observable<ReductionActionDef[]> {
    return this.http.get<ReductionActionDef[]>(`${environment.apiUrl}/reduction-actions`);
  }

  updateReductionActions(actions: ReductionActionCode[]): Observable<{ actions: string[] }> {
    const companyId = this.currentUserService.currentUser()?.companyId ?? '';
    const previous = this.reductionActions();

    this.currentUserService.patchCompany({ reductionActions: actions });

    return this.http
      .put<{ actions: string[] }>(`${environment.apiUrl}/company/${companyId}/reduction-actions`, {
        actions,
      })
      .pipe(
        tap((result) => this.currentUserService.patchCompany({ reductionActions: result.actions })),
        catchError((err) => {
          this.currentUserService.patchCompany({ reductionActions: previous });
          return throwError(() => err);
        }),
      );
  }

  getTransportAlternativeOptions(): Observable<TransportAlternativeOptions> {
    return this.http.get<TransportAlternativeOptions>(
      this.transportAlternativeOptionsUrl(),
    );
  }

  updateTransportAlternativeOptions(
    options: TransportAlternativeOptions,
  ): Observable<TransportAlternativeOptions> {
    return this.http.put<TransportAlternativeOptions>(
      this.transportAlternativeOptionsUrl(),
      options,
    );
  }

  private transportAlternativeOptionsUrl(): string {
    const companyId = this.currentUserService.currentUser()?.companyId ?? '';
    return `${environment.apiUrl}/company/${companyId}/transport-alternative-options`;
  }
}
